import React from 'react';
import { useLocation } from 'react-router-dom';
import { ScrollView, StyleSheet, View, Text } from './base_components';
import LinkHighlight from './buttons/link_highlight';
import ImageButton from './buttons/image_button';

import VolumeStore from '../stores/volume_store';
import { useOrg } from '../stores/user_store';

import util from '../tools/util';

const styles = StyleSheet.create({
  leftNav: {
    paddingTop: '1rem',
    width: 0,
    alignSelf: 'stretch',
    backgroundColor: 'var(--sidebar-bg-color)',
    borderRightWidth: 1,
    borderRightColor: 'var(--sidebar-border-color)',
    '@media (min-width: 600px)': {
      width: '25rem',
    },
  },
  closed: {
    width: '0px !important',
  },
  open: {
    '@media (max-width: 600px)': {
      width: '100vw',
    },
  },
  scroller: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    flexDirection: 'column',
  },
  scrollerInner: {
    alignSelf: 'stretch',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  listItem: {
    alignSelf: 'stretch',
    height: '3.5rem',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  text: {
    color: 'var(--sidebar-text-color)',
    fontSize: '1.5rem',
    fontWeight: '600',
  },
  childText: {
    fontWeight: '500',
  },
  active: {
    color: 'var(--link-text-color)',
    fontWeight: '600',
  },
  arrowSpacer: {
    width: '3.1rem',
    height: '3.1rem',
  },
  arrow: {
    padding: '1rem',
    width: '1.1rem',
    height: '1.1rem',
    filter: 'var(--icon-dark-grey)',
    transform: 'rotate(90deg)',
  },
  arrowClosed: {
    transform: 'rotate(0deg)',
  },
  close: {
    position: 'absolute',
    top: '0.5rem',
    right: '0.5rem',
    width: '1.5rem',
    height: '1.5rem',
    padding: '1rem',
    filter: 'var(--icon-grey)',
  },
  bottomPad: {
    height: '5rem',
  },
  verView: {
    position: 'absolute',
    bottom: 5,
    left: 5,
  },
  ver: {
    color: 'var(--sidebar-text-color)',
    fontSize: '0.8rem',
    fontWeight: '500',
  },
});
const ARROW = require('../assets/img/arrow_right_black.png');
const CLOSE = require('../assets/img/close_black.png');
const LIST = [
  {
    text: 'Volumes',
    url: '/volume',
    children: [{ insert_volumes: true }],
  },
  {
    text: 'Devices',
    url: '/device',
    have_devices: true,
  },
  {
    text: 'Users',
    url: '/admin/user',
    is_admin: true,
  },
];
type Props = {
  style?: any;
  open: boolean;
  closed: boolean;
  onClose: () => void;
};
export default function LeftNav(props: Props) {
  const { style, open, closed, onClose } = props;
  const org = useOrg();
  const location = useLocation();
  const pathname = location?.pathname;
  const volume_list = VolumeStore.useVolumeList(org?.org_id);
  React.useEffect(() => {
    VolumeStore.fetch(org?.org_id);
  }, [org?.org_id]);

  const list = LIST.slice();
  list.push({
    text: 'Organization',
    url: `/org/${org.org_id}`,
    is_admin: true,
  });

  return (
    <View
      style={[
        styles.leftNav,
        closed ? styles.closed : null,
        open ? styles.open : null,
        style,
      ]}
    >
      <View style={styles.verView}>
        <Text style={styles.ver}>{process.env.VERSION}</Text>
      </View>
      <ScrollView
        style={styles.scroller}
        contentContainerStyle={styles.scrollerInner}
      >
        {list.map((item, i) => {
          return (
            <ListEntry
              key={i}
              item={item}
              pathname={pathname}
              volumeList={volume_list}
            />
          );
        })}
        <View style={styles.bottomPad} />
      </ScrollView>
      <ImageButton style={styles.close} source={CLOSE} onPress={onClose} />
    </View>
  );
}
function ListEntry(props) {
  const { pathname, item, volumeList } = props;
  const org = useOrg();
  let content: any = null;
  if (
    (!item.is_admin || org?.is_admin) &&
    (!item.have_devices || org?.cap_add_device || org?.device_count > 0)
  ) {
    const curr_volume = volumeList?.find?.((v) =>
      pathname.startsWith(`/volume/${v.volume_id}`)
    );
    const is_active = pathname.startsWith(item.url);
    const show_arrow = item?.children?.length > 0;

    let child_active: boolean = false;
    let list: any = [];
    item.children?.forEach?.((child) => {
      const { insert_volumes } = child;
      if (insert_volumes && volumeList?.length > 0) {
        volumeList.forEach((volume) => {
          if (!volume.is_disabled && volume.is_user_volume) {
            const vol_active = volume.volume_id === curr_volume?.volume_id;
            child_active ||= vol_active;
            list.push(
              <ListItem
                key={volume.volume_id}
                url={`/volume/${volume.volume_id}/`}
                text={volume.volume_name}
                isActive={vol_active}
                isChild={true}
                showArrow={false}
              />
            );
          }
        });
      }
    });
    content = (
      <ListItem
        text={item.text}
        url={item.url}
        showArrow={show_arrow}
        isActive={is_active && !child_active}
      >
        {list}
      </ListItem>
    );
  }
  return content;
}
const g_closedMap = new Map<string, boolean>();
function ListItem(props) {
  const { text, url, showArrow, isChild, isActive } = props;
  const [is_closed, setClosed] = React.useState(g_closedMap.get(url) ?? false);

  function _onPress(e) {
    const new_val = !is_closed;
    g_closedMap.set(url, new_val);
    setClosed(new_val);
    util.stopAll(e);
  }
  return (
    <>
      <LinkHighlight
        style={styles.listItem}
        underlayColor='rgba(0,0,0,0.1)'
        url={url}
      >
        {showArrow ? (
          <ImageButton
            style={[styles.arrow, is_closed ? styles.arrowClosed : null]}
            source={ARROW}
            onPress={_onPress}
          />
        ) : (
          <View style={styles.arrowSpacer} />
        )}
        <Text
          style={[
            styles.text,
            isChild ? styles.childText : null,
            isActive ? styles.active : null,
          ]}
        >
          {text}
        </Text>
      </LinkHighlight>
      {is_closed ? null : props.children}
    </>
  );
}
